import { useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { LogIn, Search, X } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { Alert, Field, Input, Spinner } from '@/components/ui'
import { useSyncStore } from '@/lib/sync'
import { landingFor } from '@/lib/modules'
import {
  clearMustChangePassword, isSharedStartingPassword, markMustChangePassword,
} from '@/components/layout/ChangePassword'

const DOMAIN = '@ovalprojects.com'

/**
 * THE ACCOUNT LIST AND THE PRE-FILLED PASSWORD ARE OFF. (14-10-2026)
 *
 * Until 13-10-2026 both were switched on by `--mode test`, on the reasoning
 * that the test build never reached real users. On 10-10-2026 the company made
 * ims-test.ovalprojects.com the live site, built with `npm run build:test`, so
 * that reasoning stopped holding: anyone on the internet could see 56 names and
 * logins, with the shared password already in the box.
 *
 * They now need a mode of their own, `trial`, which no site is built with. Vite
 * writes the mode into the bundle as a literal, so `'test' === 'trial'` is
 * folded to `false` at build time and the minifier drops the roster, the panel
 * and the password string: they are not merely hidden, they are not in the
 * files the website sends at all. A future private trial site can bring them
 * back with `vite build --mode trial`.
 */
const SHOW_DEMO_LOGINS = import.meta.env.MODE === 'trial'

/** Empty unless the account list is switched on - see above. */
const DEFAULT_PASSWORD = SHOW_DEMO_LOGINS ? 'Oval@2026' : ''

interface DemoLogin {
  local: string
  person: string
  role: string
  site: string
  group: 'OFFICE' | 'PM' | 'RCM' | 'STORE'
}

/**
 * Every account except the System Administrator, taken from the live Sites and
 * Users masters on 08-09-2026.
 *
 * WHY THIS IS SAFE TO KEEP IN THE SOURCE
 * --------------------------------------
 * Since 14-10-2026 it is compiled ONLY with `--mode trial`, which no site uses
 * (see SHOW_DEMO_LOGINS above). Every build that reaches users - including
 * `npm run build:test`, which makes the live ims-test site - contains not a
 * single byte of it and does not pre-fill the password box.
 *
 * The System Administrator is deliberately absent. It is the one account that
 * can create users, reset passwords and reach Users & Roles, and a one-tap
 * shortcut into it is not a convenience worth having even in a test build.
 *
 * WHEN THIS COMES OUT
 * -------------------
 * Agreed for the first month or two of live use, while everyone is learning
 * the flow. Delete this array and the panel that draws it, and the login page
 * returns to being an ordinary login page.
 */
/**
 * THE FIVE HEAD OFFICE CARDS THAT POINTED AT DEAD ACCOUNTS
 * -------------------------------------------------------
 * Reported 19-09-2026, as "this is slowing our production testing".
 *
 * Five of these people have TWO accounts each. An early one under their own
 * name, created 04-08 and 12-08-2026, and the one provisioned with the rest of
 * the roster on 29-08 under a role address. Both exist in auth and both can be
 * signed into. Only the second is live:
 *
 *   Abhishek Chowdhury   procurement.mgr1   active, 93 actions
 *                        procurement.manager  INACTIVE, 0 actions
 *   Rajib Banik          procurement.mgr2   active, 148 actions
 *                        rajib.banik          INACTIVE, 0 actions
 *   Mousumi Debnath      procurement.head   active, 14 actions
 *                        mousumi.debnath      INACTIVE, 0 actions
 *   Moumita Banik        accounts.mgr1      active, 12 actions
 *                        moumita.banik        INACTIVE, 0 actions
 *   Prasenjit Debnath    accounts.mgr2      active, 1 action
 *                        prasenjit.debnath    INACTIVE, 0 actions
 *
 * This panel listed the DEAD one for all five, so one tap signed a tester in
 * as a profile marked is_active = false -- which is why half a test run would
 * behave like a different person, or not behave at all.
 *
 * Nothing was renamed to fix it. The addresses below are the ones that have
 * been doing the work since 29-08; the card was simply pointing at the wrong
 * half of a pair.
 *
 * HOW TO CHECK THIS LIST AGAIN after any account change -- paste into the SQL
 * Editor. It must return no rows:
 *
 *   with panel(local) as (values ('stores.head'),('procurement.mgr1'), ... )
 *   select pn.local, p.full_name, p.is_active
 *     from panel pn left join profiles p
 *       on split_part(p.email,'@',1) = pn.local
 *    where p.id is null or p.is_active = false;
 *
 * STILL OUTSTANDING, and not this file's to fix: the five dead accounts can
 * still be signed into. They should be disabled in Supabase Auth before go
 * live, or somebody will use one by hand.
 */
// Empty unless the account list is switched on (SHOW_DEMO_LOGINS, above), so
// that none of these names or logins reach a browser when it is off.
const ROSTER: DemoLogin[] = !SHOW_DEMO_LOGINS ? [] : [
  // ---- head office --------------------------------------------------------
  { local: 'stores.head', person: 'Ritesh Das', role: 'Head of Stores', site: 'Head Office', group: 'OFFICE' },
  { local: 'procurement.mgr1', person: 'Abhishek Chowdhury', role: 'Procurement Executive', site: 'Head Office', group: 'OFFICE' },
  { local: 'procurement.mgr2', person: 'Rajib Banik', role: 'Procurement Executive', site: 'Head Office', group: 'OFFICE' },
  { local: 'procurement.head', person: 'Mousumi Debnath', role: 'Procurement Manager', site: 'Head Office', group: 'OFFICE' },
  { local: 'accounts.mgr1', person: 'Moumita Banik', role: 'Accounts (SPOC)', site: 'Head Office', group: 'OFFICE' },
  { local: 'accounts.mgr2', person: 'Prasenjit Debnath', role: 'Accounts (SPOC)', site: 'Head Office', group: 'OFFICE' },
  { local: 'project.head', person: 'Aniruddha Majumder', role: 'Project Head', site: 'Head Office', group: 'OFFICE' },
  { local: 'cmd', person: 'Goutam Debnath', role: 'Chairman & MD', site: 'Head Office', group: 'OFFICE' },
  { local: 'cxo', person: 'CXO', role: 'CXO (View Only)', site: 'Head Office', group: 'OFFICE' },
  { local: 'procandlogs', person: 'Akash Debnath', role: 'Stores Executive (HO)', site: 'Head Office', group: 'OFFICE' },
  // ---- project managers ---------------------------------------------------
  { local: 'anup.sarkar', person: 'Anup Sarkar', role: 'Project Manager', site: 'Unity Mall', group: 'PM' },
  { local: 'joydeep.sarkar', person: 'Joydeep Sarkar', role: 'Project Manager', site: 'Nagicherra Road', group: 'PM' },
  { local: 'kinshuk.datta', person: 'Kinshuk Datta', role: 'Project Manager', site: '27 sites', group: 'PM' },
  { local: 'rocky.biswas', person: 'Rocky Biswas', role: 'Project Manager', site: '5 sites', group: 'PM' },
  { local: 'sunny.ghosh', person: 'Sunny Ghosh', role: 'Project Manager', site: '3 sites', group: 'PM' },
  // ---- resident construction managers, one per site -----------------------
  { local: 'asharambari.rcm', person: 'Kinshuk Dutta', role: 'Resident Construction Manager', site: 'Asharambari HS School', group: 'RCM' },
  { local: 'avangacherra.rcm', person: 'Motilal Debbarma', role: 'Resident Construction Manager', site: 'Avangacherra Park', group: 'RCM' },
  { local: 'bachaibari.rcm', person: 'Gandhi Debbarma', role: 'Resident Construction Manager', site: 'Bachaibari HS School', group: 'RCM' },
  { local: 'baishnabpur.rcm', person: 'Tushar Kanti Das', role: 'Resident Construction Manager', site: 'Baishnabpur High School', group: 'RCM' },
  { local: 'bashi.rcm', person: 'Surajit Das', role: 'Resident Construction Manager', site: 'Bashi Chandra Para HS School', group: 'RCM' },
  { local: 'behalabari.rcm', person: 'Biswajit Debnath', role: 'Resident Construction Manager', site: 'Behalabari HS School', group: 'RCM' },
  { local: 'chellagang.rcm', person: 'Prabir Panda', role: 'Resident Construction Manager', site: 'Chlakaham HS School', group: 'RCM' },
  { local: 'dharmanagar.rcm', person: 'Pancham De', role: 'Resident Construction Manager', site: 'Dharmanagar Market', group: 'RCM' },
  { local: 'ekjatan.rcm', person: 'Ujjwal Halder', role: 'Resident Construction Manager', site: 'EK Jatan Kumar HS School', group: 'RCM' },
  { local: 'ghorakappa.rcm', person: 'Atanu Bhowmik', role: 'Resident Construction Manager', site: 'Ghorakappa HS School', group: 'RCM' },
  { local: 'girishchandra.rcm', person: 'Sagar Shil', role: 'Resident Construction Manager', site: 'Girish Chandra Karbari Para HS School', group: 'RCM' },
  { local: 'khowaird.rcm', person: 'Sujoy Ghosh', role: 'Resident Construction Manager', site: 'Khowai Stromwater Drainage & Roads', group: 'RCM' },
  { local: 'khubal.rcm', person: 'Ujjwal Halder', role: 'Resident Construction Manager', site: 'Khubal ONGC GCS', group: 'RCM' },
  { local: 'nitswimming.rcm', person: 'Kinshuk Dutta', role: 'Resident Construction Manager', site: 'NIT Tripura Swimming Pool', group: 'RCM' },
  { local: 'nagicherra.rcm', person: 'Amit Kumar', role: 'Resident Construction Manager', site: 'Nagicherra Road', group: 'RCM' },
  { local: 'ho.rcm', person: 'Kinshuk Dutta', role: 'Resident Construction Manager', site: 'OVAL Head Office', group: 'RCM' },
  { local: 'ranirbazar.rcm', person: 'Iskon Debnath', role: 'Resident Construction Manager', site: 'Ranirbazar Stromwater Drainage & Roads', group: 'RCM' },
  { local: 'mohanpur.rcm', person: 'Asit Ghosh', role: 'Resident Construction Manager', site: 'Sidhai Mohanpur Stromwater Drainage & Roads', group: 'RCM' },
  { local: 'takarjala.rcm', person: 'Sabyasachi Datta Gupta', role: 'Resident Construction Manager', site: 'Takarjala ST Boys\' Hostel', group: 'RCM' },
  { local: 'unitymall.rcm', person: 'Sankar Podder', role: 'Resident Construction Manager', site: 'Unity Mall', group: 'RCM' },
  { local: 'vidyasagar.rcm', person: 'Sujan Chakraborty', role: 'Resident Construction Manager', site: 'Vidyasagar Market', group: 'RCM' },
  // ---- site store-men, one per site ---------------------------------------
  { local: 'asharambari.store', person: 'Kinshuk Dutta Asa', role: 'Site Store-man', site: 'Asharambari HS School', group: 'STORE' },
  { local: 'avangacherra.store', person: 'Motilal Debbarma', role: 'Site Store-man', site: 'Avangacherra Park', group: 'STORE' },
  { local: 'bachaibari.store', person: 'Samarjit Debnath', role: 'Site Store-man', site: 'Bachaibari HS School', group: 'STORE' },
  { local: 'baishnabpur.store', person: 'Tushar Kanti Das', role: 'Site Store-man', site: 'Baishnabpur High School', group: 'STORE' },
  { local: 'bashi.store', person: 'Surajit Das', role: 'Site Store-man', site: 'Bashi Chandra Para HS School', group: 'STORE' },
  { local: 'behalabari.store', person: 'Biswajit Debnath', role: 'Site Store-man', site: 'Behalabari HS School', group: 'STORE' },
  { local: 'chellagang.store', person: 'Goutam Biswas', role: 'Site Store-man', site: 'Chlakaham HS School', group: 'STORE' },
  { local: 'dharmanagar.store', person: 'Pancham De', role: 'Site Store-man', site: 'Dharmanagar Market', group: 'STORE' },
  { local: 'ekjatan.store', person: 'Vivek Saha', role: 'Site Store-man', site: 'EK Jatan Kumar HS School', group: 'STORE' },
  { local: 'ghorakappa.store', person: 'Atanu Bhowmik', role: 'Site Store-man', site: 'Ghorakappa HS School', group: 'STORE' },
  { local: 'girishchandra.store', person: 'Swapan Biswas', role: 'Site Store-man', site: 'Girish Chandra Karbari Para HS School', group: 'STORE' },
  { local: 'khowaird.store', person: 'Animesh Mandal', role: 'Site Store-man', site: 'Khowai Stromwater Drainage & Roads', group: 'STORE' },
  { local: 'khubal.store', person: 'Ujjwal Halder', role: 'Site Store-man', site: 'Khubal ONGC GCS', group: 'STORE' },
  { local: 'nitswimming.store', person: 'Sindhu Ghosh', role: 'Site Store-man', site: 'NIT Tripura Swimming Pool', group: 'STORE' },
  { local: 'nagicherra.store', person: 'Biswajit Nath', role: 'Site Store-man', site: 'Nagicherra Road', group: 'STORE' },
  { local: 'ranirbazar.store', person: 'Bijoy Das', role: 'Site Store-man', site: 'Ranirbazar Stromwater Drainage & Roads', group: 'STORE' },
  { local: 'mohanpur.store', person: 'Kamal Dhar', role: 'Site Store-man', site: 'Sidhai Mohanpur Stromwater Drainage & Roads', group: 'STORE' },
  { local: 'takarjala.store', person: 'Roopjit Sarkar', role: 'Site Store-man', site: 'Takarjala ST Boys\' Hostel', group: 'STORE' },
  { local: 'unitymall.store', person: 'Santanu Das', role: 'Site Store-man', site: 'Unity Mall', group: 'STORE' },
  { local: 'vidyasagar.store', person: 'Sujan Chakraborty', role: 'Site Store-man', site: 'Vidyasagar Market', group: 'STORE' },
]

const GROUPS: Array<{ key: DemoLogin['group'] | 'ALL'; label: string; dot: string }> = [
  { key: 'ALL',    label: 'Everyone',            dot: 'bg-slate-400' },
  { key: 'OFFICE', label: 'Head office',         dot: 'bg-oval-600' },
  { key: 'PM',     label: 'Project Managers',    dot: 'bg-amber-500' },
  { key: 'RCM',    label: 'Site RCMs',           dot: 'bg-emerald-600' },
  { key: 'STORE',  label: 'Site store-men',      dot: 'bg-sky-600' },
]

/** The band of colour down the left of a card, so a role is recognised before it is read. */
const GROUP_TONE: Record<DemoLogin['group'], { bar: string; chip: string }> = {
  OFFICE: { bar: 'bg-oval-600',    chip: 'bg-oval-50 text-oval-700' },
  PM:     { bar: 'bg-amber-500',   chip: 'bg-amber-50 text-amber-700' },
  RCM:    { bar: 'bg-emerald-600', chip: 'bg-emerald-50 text-emerald-700' },
  STORE:  { bar: 'bg-sky-600',     chip: 'bg-sky-50 text-sky-700' },
}

/** "Sabyasachi Datta Gupta" -> "SD". Two letters is all a 34px circle holds. */
const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()

export function Login() {
  const { profile, signIn, loading } = useAuth()
  const online = useSyncStore((s) => s.online)
  // Only the part before the @ is typed. Paste a full address and it is used
  // as it stands, so this never gets in the way of a real sign-in.
  const [local, setLocal] = useState('')
  const [password, setPassword] = useState(SHOW_DEMO_LOGINS ? DEFAULT_PASSWORD : '')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Open by default when the account list is switched on (trial builds only):
  // the whole point of the switcher is that nobody should have to look for it.
  const [showHelp, setShowHelp] = useState(SHOW_DEMO_LOGINS)
  const [query, setQuery] = useState('')
  const [group, setGroup] = useState<DemoLogin['group'] | 'ALL'>('ALL')

  // Fifty-six accounts is too many to scan, so the search is the primary
  // control and the chips are the shortcut. One pass over a fixed array —
  // memoised because it runs on every keystroke.
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return ROSTER.filter((r) => (group === 'ALL' || r.group === group)
      && (!q || `${r.person} ${r.role} ${r.site} ${r.local}`.toLowerCase().includes(q)))
  }, [query, group])

  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: ROSTER.length }
    for (const r of ROSTER) c[r.group] = (c[r.group] ?? 0) + 1
    return c
  }, [])

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center"><Spinner className="h-6 w-6 text-oval-600" /></div>
  }
  // Not '/' any more. Where a sign-in lands now depends on which sides this
  // person is entitled to and where they were last - see landingFor() in
  // lib/modules.ts. Somebody entitled to Materials only still goes straight to
  // '/', exactly as before, and never meets the chooser.
  if (profile) return <Navigate to={landingFor(profile.role)} replace />

  const fullEmail = local.includes('@') ? local.trim() : `${local.trim()}${DOMAIN}`

  /*
   * The one place in the application that still DRAWS an email address, and
   * deliberately so.
   *
   * It is your own, echoed back as you type it: the box takes the part before
   * the @ and the app appends the domain, so without this you cannot see what
   * you are actually signing in as. Nobody else's address is exposed and there
   * is no signed-in user yet — the rule management set on 01-09-2026 is about
   * naming OTHER people in trails and registers.
   *
   * Hoisted out of the JSX so the exemption can be marked on the line itself,
   * which is where scripts/check-no-emails-on-screen.py looks for it. An
   * exemption should be a visible decision in the source, not a quiet widening
   * of the checker's regex.
   */
  const signingInAs = local.trim() ? `Signing in as ${fullEmail}` : undefined // check-no-emails-on-screen: allow

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!local.trim()) { setError('Enter the part of the address before the @.'); return }
    setBusy(true)
    // 14-10-2026: somebody signing in with the shared starting password must
    // set their own before using the app (ForcedPasswordGate). Decided BEFORE
    // signing in, from a fingerprint of what was typed, so the gate is already
    // armed when the app opens. Only the email is remembered, never a password.
    const shared = await isSharedStartingPassword(password)
    if (shared) markMustChangePassword(fullEmail)
    try {
      await signIn(fullEmail, password)
    } catch (err) {
      if (shared) clearMustChangePassword()
      setError(err instanceof Error ? err.message : 'Sign in failed')
    } finally {
      setBusy(false)
    }
  }

  /**
   * One tap signs in. It does not merely fill the boxes.
   *
   * Filling the boxes and then asking for a second tap on Sign in is the thing
   * that made the old panel tedious — every account switch was scroll, tap,
   * move to the button, tap. The address and password are both known here, so
   * there is nothing left to decide.
   *
   * The fields are filled first anyway, so a failure leaves the form ready to
   * retry by hand rather than blank.
   */
  async function pick(row: DemoLogin) {
    setLocal(row.local)
    setPassword(DEFAULT_PASSWORD)
    setError(null)
    setBusy(true)
    try {
      await signIn(`${row.local}${DOMAIN}`, DEFAULT_PASSWORD)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-gradient-to-br from-slate-100 via-slate-100 to-oval-50 px-4 py-8">
      {/* The page is a narrow sign-in column until the switcher is opened, and
          then it becomes as wide as the screen allows — which is what turns a
          scroll of 56 rows into a grid you can take in at a glance. */}
      <div className={`w-full space-y-4 ${showHelp && SHOW_DEMO_LOGINS ? 'max-w-6xl' : 'max-w-md'}`}>
        <div className={showHelp && SHOW_DEMO_LOGINS ? 'mx-auto w-full max-w-md space-y-4' : 'space-y-4'}>
        <div className="text-center">
          <img src="/logo-oval.png" alt="OVAL Projects Engineering Limited" className="mx-auto h-16 w-16 object-contain" />
          <h1 className="mt-3 text-xl font-bold text-oval-800">OVAL PROJECTS ENGINEERING LIMITED</h1>
          <p className="text-sm text-slate-500">Procurement, Stores &amp; Inventory Management</p>
        </div>

        {!online && (
          <Alert tone="warning" title="You are offline">
            Sign in needs a connection the first time on this device. Once you have signed in here,
            the app keeps working without a network.
          </Alert>
        )}

        <form onSubmit={onSubmit} className="card card-pad space-y-4">
          {error && <Alert tone="error">{error}</Alert>}

          <Field label="Email address" required hint={signingInAs}>
            <div className="flex items-stretch">
              <input
                type="text" autoComplete="username" required autoFocus
                value={local} onChange={(e) => setLocal(e.target.value)}
                placeholder="stores.head"
                className="field rounded-r-none border-r-0 text-right"
              />
              <span className="inline-flex select-none items-center whitespace-nowrap rounded-r-lg border border-l-0 border-slate-300 bg-slate-50 px-3 font-mono text-sm text-slate-500">
                {DOMAIN}
              </span>
            </div>
          </Field>

          <Field label="Password" required>
            <Input
              type="password" autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </Field>

          <button type="submit" className="btn-primary w-full" disabled={busy}>
            {busy ? <Spinner /> : <LogIn className="h-4 w-4" />} Sign in
          </button>

          <p className="text-center text-xs text-slate-500">
            Accounts are created by the system administrator. There is no self sign-up.
          </p>
        </form>
        </div>

        {SHOW_DEMO_LOGINS && (
          <div className="card card-pad">
            <button
              className="flex w-full items-center justify-between text-left text-sm font-semibold text-slate-700"
              onClick={() => setShowHelp((s) => !s)}
              type="button"
            >
              <span>
                Sign in as someone else
                <span className="ml-2 font-normal text-slate-400">{ROSTER.length} accounts</span>
              </span>
              <span className="text-xs font-normal text-slate-400">{showHelp ? 'hide' : 'show'}</span>
            </button>

            {showHelp && (
              <div className="mt-3 space-y-3">
                {/* ---- search and role chips, sticky so they stay reachable ---- */}
                <div className="sticky top-0 z-10 -mx-1 space-y-2 bg-white/95 px-1 pb-2 pt-1 backdrop-blur">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                      placeholder="Type a name, a role or a site — Vidyasagar, store-man, Ritesh…"
                      className="field pl-9 pr-9"
                    />
                    {query && (
                      <button type="button" onClick={() => setQuery('')}
                              title="Clear the search"
                              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {GROUPS.map((g) => (
                      <button key={g.key} type="button" onClick={() => setGroup(g.key)}
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${
                                group === g.key
                                  ? 'border-oval-500 bg-oval-50 font-medium text-oval-800'
                                  : 'border-slate-200 text-slate-600 hover:border-oval-300 hover:bg-oval-50'}`}>
                        <span className={`h-2 w-2 shrink-0 rounded-full ${g.dot}`} />
                        {g.label}
                        <span className="text-slate-400">{counts[g.key] ?? 0}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* ---- the accounts ------------------------------------------
                    Two columns on a phone, six on a desktop. Every card is the
                    same height whatever the length of the site name, so the
                    grid reads as a grid and not as a ragged wall. */}
                {shown.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-500">
                    Nobody matches “{query}”. Clear the search, or pick a different role.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                    {shown.map((r) => {
                      const tone = GROUP_TONE[r.group]
                      const active = local === r.local
                      return (
                        <button
                          key={r.local}
                          type="button"
                          disabled={busy}
                          onClick={() => void pick(r)}
                          title={`Sign in as ${r.person} — ${r.role}, ${r.site}`}
                          className={`group relative flex h-full flex-col overflow-hidden rounded-lg border px-2.5 py-2 pl-3.5 text-left transition disabled:opacity-60 ${
                            active
                              ? 'border-oval-500 bg-oval-50 ring-1 ring-oval-400'
                              : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:border-oval-300 hover:shadow-sm'}`}
                        >
                          <span className={`absolute inset-y-0 left-0 w-1.5 ${tone.bar}`} />
                          <span className="flex items-start gap-2">
                            <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${tone.chip}`}>
                              {initials(r.person)}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-xs font-semibold text-slate-800">{r.person}</span>
                              <span className="block truncate text-[11px] text-slate-500">{r.role}</span>
                            </span>
                          </span>
                          <span className="mt-1.5 block truncate text-[11px] text-slate-500" title={r.site}>
                            {r.site}
                          </span>
                          <span className="mt-auto block truncate pt-1 font-mono text-[10px] text-slate-400">
                            {r.local}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}

                <p className="text-[11px] text-slate-400">
                  One tap signs you straight in — every account uses{' '}
                  <code className="rounded bg-slate-100 px-1 font-mono">{DEFAULT_PASSWORD}</code>.
                  The System Administrator is not on this list and never will be.
                  This panel is compiled into the test build only; the live site shows neither the
                  accounts nor a pre-filled password.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
